// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsSims from '../../../api/actionsSims';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Select from '@/common-utils/components/ui/select';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.simCardId
 * @param root0.setLaunchRefetch
 * @returns {JSX.Element} ModalResetPassword Component
 */
function CustomPayModalActivateSimCard({ isOpen, setIsOpen, simCardId, setLaunchRefetch }) {
    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.activate.steps';

    // Toast
    const { toast } = useToast();

    // State
    const [step, setStep] = useState(0);
    const [selectedOffer, setSelectedOffer] = useState(null);

    // API Calls terminate SimCard
    const { data, isLoading } = actionsSims.getSimPayviewOffers();

    const onSuccessActivateSimCard = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity={'modal.activate.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
    };

    const onErrorActivateSimCard = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.activate.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };

    const submitActivateSimCard = actionsSims.activateSimCard(onSuccessActivateSimCard, onErrorActivateSimCard);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.offer_selection`, disabledNext: !selectedOffer },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [selectedOffer]
    );

    //  Constants
    const offersOptions = data?.map(({ uuid, name }) => ({ value: uuid, name: name })) ?? [];
    const selectedOfferName = data?.find((item) => item.uuid === selectedOffer)?.name ?? '';

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-2xl'>
                <HeaderTitle title={'modal.activate.title'} namespace={nsSimCard} />
                <ModalBody>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {step === 0 &&
                        <div className='my-4 sm:px-48'>
                            <div className='text-center mb-4'>
                                <Entity entity='modal.activate.selection.label' namespace={nsSimCard} />
                            </div>
                            {isLoading && <Spinner className='panel-spinner' />}
                            {data &&
                                <Select
                                    value={selectedOffer}
                                    options={offersOptions}
                                    onChange={(value) => setSelectedOffer(value)}
                                    className='flex-1'
                                    placeholder='modal.activate.selection.placeholder'
                                    namespace={nsSimCard}
                                />
                            }
                        </div>
                    }
                    {step === 1 &&
                        <div className='text-center my-4 space-y-2'>
                            <div>
                                <Entity entity='modal.activate.confirmation.text_1' data={{ offer: selectedOfferName }} namespace={nsSimCard} />
                            </div>
                            <div>
                                <Entity entity='modal.activate.confirmation.text_2' namespace={nsSimCard} />
                            </div>
                            <div>
                                <Entity entity='modal.activate.confirmation.text_3' namespace={nsSimCard} />
                            </div>
                        </div>
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    isLoading={submitActivateSimCard.isLoading}
                    onValidate={() => {
                        submitActivateSimCard.mutate({
                            offer: selectedOffer,
                            uuids: [simCardId]
                        });
                    }}
                    disableValidation={disabledNext}
                />
            </ModalContent>
        </Modal>
    );
}

export default CustomPayModalActivateSimCard;

CustomPayModalActivateSimCard.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    simCardId: PropTypes.string,
    setLaunchRefetch: PropTypes.func.isRequired
};